import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import * as P from '@/constants/paths';
import useRegisterStore, { RegistrationTrack } from '../useRegistrationStore';

const defaultSchema = z.object({
  firstName: z.string().min(1, { message: 'first name is required' }),
  lastName: z.string().min(1, { message: 'last name is required' }),
  birthDay: z
    .date()
    .nullable()
    .refine((date) => date instanceof Date && !isNaN(date?.getTime()), {
      message: 'pick a date',
    })
    .refine(
      (date) =>
        date instanceof Date &&
        date <= new Date() &&
        date >= new Date('1900-01-01'),
      {
        message: 'pick a date',
      },
    ),
  gender: z.string().min(1, { message: 'Gender is required' }),
});

type PersonalInfoFormData = z.infer<typeof defaultSchema>;

const usePersonalInfo = () => {
  const navigate = useNavigate();
  const {
    firstName,
    lastName,
    dob,
    gender,
    setPersonalInformation,
    setRestrictedRegistrationSteps,
  } = useRegisterStore();

  const form = useForm<PersonalInfoFormData>({
    mode: 'onSubmit',
    resolver: zodResolver(defaultSchema),
    defaultValues: {
      firstName: firstName,
      lastName: lastName,
      // Show API UTC-midnight DOB as the same calendar day in local UI
      birthDay: dob
        ? (() => {
            const date = new Date(dob);
            if (
              /^\d{4}-\d{2}-\d{2}$/.test(dob) ||
              dob.endsWith('T00:00:00.000Z')
            ) {
              return new Date(
                date.getUTCFullYear(),
                date.getUTCMonth(),
                date.getUTCDate(),
              );
            }
            return date;
          })()
        : null,
      gender: gender,
    },
  });

  const onSubmit = (input: {
    firstName: string;
    lastName: string;
    birthDay: Date;
    gender: string;
  }) => {
    Promise.all([
      setPersonalInformation(
        input.firstName,
        input.lastName,
        input.birthDay.toISOString(),
        input.gender,
      ),
      setRestrictedRegistrationSteps([
        RegistrationTrack.AddressConfirmation,
        RegistrationTrack.RegistrationConfirmation,
        RegistrationTrack.TermsOfService,
        RegistrationTrack.ConsentForm,
      ]),
      navigate(P.REGISTRATION_CONTACT_INFORMATION),
    ]);
  };

  return {
    form,
    onSubmit,
  };
};

export default usePersonalInfo;
